import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Container,
  Grid,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Group as GroupIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  ExitToApp as LeaveIcon,
  Schedule as PendingIcon,
  CheckCircle as JoinedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSafeBack } from '../hooks/useSafeBack';
import { toast } from 'react-toastify';
import teamService, { Team } from '../services/teamService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const MyGroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useSafeBack('/dashboard');
  const [joinedTeams, setJoinedTeams] = useState<Team[]>([]);
  const [pendingTeams, setPendingTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [leavingTeam, setLeavingTeam] = useState<number | null>(null);
  const [cancellingRequest, setCancellingRequest] = useState<number | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; teamId: number | null; teamName: string }>({
    open: false,
    teamId: null,
    teamName: ''
  });
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    let mounted = true;
    
    const loadTeamsData = async () => {
      try {
        setLoading(true);
        const [joinedResponse, pendingResponse] = await Promise.all([
          teamService.getJoinedTeams(),
          teamService.getPendingRequests()
        ]);

        if (mounted) {
          setJoinedTeams(joinedResponse);
          setPendingTeams(pendingResponse);
        }
      } catch (error) {
        console.error('Error loading teams:', error);
        if (mounted) {
          toast.error('Failed to load teams');
          setJoinedTeams([]);
          setPendingTeams([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    
    loadTeamsData();
    
    return () => {
      mounted = false;
    };
  }, []);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const [joinedResponse, pendingResponse] = await Promise.all([
        teamService.getJoinedTeams(),
        teamService.getPendingRequests()
      ]);

      setJoinedTeams(joinedResponse);
      setPendingTeams(pendingResponse);
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('Failed to load teams');
      setJoinedTeams([]);
      setPendingTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveTeam = async () => {
    if (!confirmDialog.teamId) return;

    try {
      setLeavingTeam(confirmDialog.teamId);
      await teamService.leaveTeam(confirmDialog.teamId);
      toast.success('Successfully left the group');
      setConfirmDialog({ open: false, teamId: null, teamName: '' });
      await loadTeams();
    } catch (error: any) {
      console.error('Error leaving team:', error);
      toast.error(error.response?.data?.message || 'Failed to leave team');
    } finally {
      setLeavingTeam(null);
    }
  };

  const handleCancelRequest = async (teamId: number) => {
    try {
      setCancellingRequest(teamId);
      await teamService.cancelJoinRequest(teamId);
      toast.success('Join request cancelled');
      await loadTeams();
    } catch (error) {
      console.error('Error cancelling request:', error);
      toast.error('Failed to cancel request');
    } finally {
      setCancellingRequest(null);
    }
  };

  const openConfirmDialog = (teamId: number, teamName: string) => {
    setConfirmDialog({ open: true, teamId, teamName });
  };

  const renderTeamCard = (team: Team, isPending: boolean = false) => {
    const isProcessing = isPending 
      ? cancellingRequest === team.id 
      : leavingTeam === team.id;

    return (
      <Card key={team.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Typography variant="h6" component="h2">
              {team.name}
            </Typography>
            <Chip 
              icon={isPending ? <PendingIcon /> : <JoinedIcon />} 
              label={isPending ? 'Pending' : 'Member'} 
              color={isPending ? 'warning' : 'success'} 
              size="small" 
            />
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            {team.description || 'No description available'}
          </Typography>

          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <SchoolIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {team.departmentName || 'General'}
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {team.memberCount} / {team.maxMembers} members
              </Typography>
            </Box>
            
            {team.organizationName && (
              <Box display="flex" alignItems="center" gap={1}>
                <GroupIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {team.organizationName}
                </Typography>
              </Box>
            )}
            
            {team.createdByType === 'provider' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Provider-led team
              </Typography>
            )}
          </Box>
        </CardContent>

        <CardActions>
          {isPending ? (
            <Button 
              size="small" 
              color="warning"
              onClick={() => handleCancelRequest(team.id)}
              disabled={isProcessing}
              fullWidth
              startIcon={isProcessing ? <CircularProgress size={16} /> : null}
            >
              {isProcessing ? 'Cancelling...' : 'Cancel Request'}
            </Button>
          ) : (
            <Button 
              size="small" 
              color="error"
              onClick={() => openConfirmDialog(team.id, team.name)}
              disabled={isProcessing}
              fullWidth
              startIcon={<LeaveIcon />}
            >
              Leave Group
            </Button>
          )}
        </CardActions>
      </Card>
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button 
            variant="outlined" 
            onClick={goBack}
            size="small"
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            My Groups
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          onClick={() => navigate('/groups/join')}
          startIcon={<GroupIcon />}
        >
          Join New Group
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab 
            label={
              <Badge badgeContent={joinedTeams.length} color="primary">
                Joined Groups
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={pendingTeams.length} color="warning">
                Pending Requests
              </Badge>
            } 
          />
        </Tabs>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TabPanel value={tabValue} index={0}>
            {joinedTeams.length === 0 ? (
              <Alert severity="info">
                You haven't joined any groups yet. 
                <Button 
                  size="small" 
                  onClick={() => navigate('/groups/join')}
                  sx={{ ml: 2 }}
                >
                  Browse Groups
                </Button>
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {joinedTeams.map((team) => (
                  <Grid item xs={12} sm={6} md={4} key={team.id}>
                    {renderTeamCard(team)}
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {pendingTeams.length === 0 ? (
              <Alert severity="info">
                You don't have any pending group requests.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {pendingTeams.map((team) => (
                  <Grid item xs={12} sm={6} md={4} key={team.id}>
                    {renderTeamCard(team, true)}
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>
        </>
      )}

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, teamId: null, teamName: '' })}
      >
        <DialogTitle>Leave Group?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to leave "{confirmDialog.teamName}"? 
            You'll need to request access again if you want to rejoin.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, teamId: null, teamName: '' })}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleLeaveTeam} 
            color="error"
            disabled={leavingTeam !== null}
          >
            {leavingTeam !== null ? <CircularProgress size={20} /> : 'Leave Group'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyGroupsPage;